import { ThrowLiteError } from '../lite-error.js';
import { F32 } from '../engine/typed-arrays.js';

function buildRuntimeThinMesh(scene, mesh, pending, material = mesh.material) {
  return import('../scene/scene-runtime-mesh-build.js').then((module) => module.A(scene, material, mesh, pending)).catch((error) => console.error(error));
}
function setThinInstances(mesh, matrices, count) {
  mesh._runtimeThinBuild = buildRuntimeThinMesh;
  if (!mesh.thinInstances) {
    mesh.thinInstances = {
      matrices,
      count,
      _capacity: count,
      _version: 1,
      _gpuBuffer: null,
      _gpuBufferStorage: false,
      _gpuVersion: 0,
      _dirtyMin: 0,
      _dirtyMax: count,
      _colorVersion: 0,
      _colorDirtyMin: 0,
      _colorDirtyMax: 0,
      _colorGpuBuffer: null,
      _colorGpuBufferStorage: false,
      _colorGpuVersion: 0,
      _gpuCullingEnabled: false
    };
  } else {
    mesh.thinInstances.matrices = matrices;
    mesh.thinInstances.count = count;
    mesh.thinInstances._capacity = count;
    mesh.thinInstances._version++;
    mesh.thinInstances._dirtyMin = 0;
    mesh.thinInstances._dirtyMax = count;
  }
}
function setThinInstanceCount(mesh, count) {
  const ti = mesh.thinInstances;
  if (!ti) {
    return;
  }
  ti.count = count;
  ti._version++;
  ti._dirtyMin = 0;
  ti._dirtyMax = count;
}
function setThinInstanceDrawCount(mesh, count) {
  const ti = mesh.thinInstances;
  if (!ti) {
    return;
  }
  if (!Number.isInteger(count) || count < 0 || count > ti._capacity) {
    throw new RangeError(`Thin instance draw count ${count} must be an integer between 0 and capacity ${ti._capacity}`);
  }
  if (ti.count === count) {
    return;
  }
  if (!ti._gpuBuffer || ti._gpuVersion !== ti._version || ti.colors && (!ti._colorGpuBuffer || ti._colorGpuVersion !== ti._colorVersion)) {
    ThrowLiteError(369);
  }
  ti.count = count;
  ti._gpuVersion = ++ti._version;
}
function enableThinInstanceDynamicDrawCount(mesh) {
  const ti = mesh.thinInstances;
  if (!ti) {
    ThrowLiteError(370);
  }
  if (!ti._drawArgsBuffer) {
    ti._drawArgsInstanceCount = -1;
  }
}
function addThinInstance(mesh, matrix) {
  mesh._runtimeThinBuild = buildRuntimeThinMesh;
  const ti = mesh.thinInstances;
  if (!ti) {
    const capacity = 16;
    const matrices = new F32(capacity * 16);
    matrices.set(matrix, 0);
    mesh.thinInstances = {
      matrices,
      count: 1,
      _capacity: capacity,
      _version: 1,
      _gpuBuffer: null,
      _gpuBufferStorage: false,
      _gpuVersion: 0,
      _dirtyMin: 0,
      _dirtyMax: 1,
      _colorVersion: 0,
      _colorDirtyMin: 0,
      _colorDirtyMax: 0,
      _colorGpuBuffer: null,
      _colorGpuBufferStorage: false,
      _colorGpuVersion: 0,
      _gpuCullingEnabled: false
    };
    return 0;
  }
  const index = ti.count;
  if (index >= ti._capacity) {
    const newCap = ti._capacity * 2;
    const newData = new F32(newCap * 16);
    newData.set(ti.matrices);
    ti.matrices = newData;
    ti._capacity = newCap;
  }
  ti.matrices.set(matrix, index * 16);
  ti.count++;
  ti._version++;
  ti._dirtyMin = 0;
  ti._dirtyMax = ti.count;
  return index;
}
function setThinInstanceMatrix(mesh, index, matrix) {
  const ti = mesh.thinInstances;
  ti.matrices.set(matrix, index * 16);
  ti._version++;
  ti._dirtyMin = Math.min(ti._dirtyMin, index);
  ti._dirtyMax = Math.max(ti._dirtyMax, index + 1);
}
function removeThinInstance(mesh, index) {
  const ti = mesh.thinInstances;
  const last = ti.count - 1;
  if (index !== last) {
    ti.matrices.copyWithin(index * 16, last * 16, last * 16 + 16);
  }
  ti.count--;
  ti._version++;
  ti._dirtyMin = 0;
  ti._dirtyMax = ti.count;
}
function flushThinInstances(mesh) {
  const ti = mesh.thinInstances;
  ti._version++;
  ti._dirtyMin = 0;
  ti._dirtyMax = ti.count;
}
function setThinInstanceColors(mesh, colors) {
  const ti = mesh.thinInstances;
  ti.colors = colors;
  ti._version++;
  ti._colorVersion++;
  ti._colorDirtyMin = 0;
  ti._colorDirtyMax = ti.count;
}
function setThinInstanceColor(mesh, index, r, g, b, a) {
  const ti = mesh.thinInstances;
  const c = ti.colors;
  const o = index * 4;
  c[o] = r;
  c[o + 1] = g;
  c[o + 2] = b;
  c[o + 3] = a;
  ti._version++;
  ti._colorVersion++;
  ti._colorDirtyMin = Math.min(ti._colorDirtyMin, index);
  ti._colorDirtyMax = Math.max(ti._colorDirtyMax, index + 1);
}
function enableThinInstanceGpuCulling(mesh, enabled = true) {
  const ti = mesh.thinInstances;
  if (!ti) {
    ThrowLiteError(371);
  }
  if (ti._gpuCullingEnabled === enabled) {
    return;
  }
  ti._gpuCullingEnabled = enabled;
  ti._gpuVersion = -1;
  ti._colorGpuVersion = -1;
}
function setThinInstanceCullBoundsPad(mesh, pad) {
  const ti = mesh.thinInstances;
  if (!ti) {
    ThrowLiteError(372);
  }
  ti._cullBoundsPad = pad;
}
function setThinInstanceLodPartner(fullMesh, lodMesh, options) {
  const ti = fullMesh.thinInstances;
  const lodTi = lodMesh.thinInstances;
  if (!ti || !lodTi) {
    ThrowLiteError(373);
  }
  if (fullMesh === lodMesh) {
    ThrowLiteError(374);
  }
  if ((ti._refCount ?? 1) > 1 || (lodTi._refCount ?? 1) > 1) {
    ThrowLiteError(375);
  }
  if (!Number.isFinite(options.distance) || options.distance < 0) {
    throw new RangeError("setThinInstanceLodPartner distance must be a finite non-negative number");
  }
  const band = options.band ?? 0;
  if (!Number.isFinite(band) || band < 0) {
    throw new RangeError("setThinInstanceLodPartner band must be a finite non-negative number");
  }
  if (ti._lodSource) {
    ThrowLiteError(376);
  }
  if (lodTi._lodPartner) {
    ThrowLiteError(377);
  }
  if (lodTi._lodSource && lodTi._lodSource !== fullMesh) {
    ThrowLiteError(378);
  }
  if (lodTi.colors && !ti.colors) {
    ThrowLiteError(379);
  }
  const previous = ti._lodPartner;
  if (previous && previous !== lodMesh) {
    const prevTi = previous.thinInstances;
    if (prevTi && prevTi._lodSource === fullMesh) {
      releaseLodConsumer(prevTi);
    }
    previous._clone = void 0;
    ti._lodBuckets = null;
  }
  ti._lodPartner = lodMesh;
  ti._lodDistance = options.distance;
  ti._lodBand = band;
  lodTi._lodSource = fullMesh;
  fullMesh._clone = lodMesh._clone = "Cannot clone LOD-paired mesh";
  const buckets = ti._lodBuckets ?? /* @__PURE__ */ new WeakMap();
  ti._lodBuckets = buckets;
  lodTi._lodBuckets = buckets;
  if (!lodTi._gpuCullingEnabled) {
    lodTi._lodAutoCull = true;
    lodTi._gpuCullingEnabled = true;
    lodTi._gpuVersion = -1;
    lodTi._colorGpuVersion = -1;
  }
}
function clearThinInstanceLodPartner(fullMesh) {
  const ti = fullMesh.thinInstances;
  if (!ti?._lodPartner) {
    return;
  }
  const lodMesh = ti._lodPartner;
  const lodTi = lodMesh.thinInstances;
  ti._lodPartner = null;
  ti._lodDistance = void 0;
  ti._lodBand = void 0;
  ti._lodBuckets = null;
  fullMesh._clone = void 0;
  lodMesh._clone = void 0;
  if (lodTi && lodTi._lodSource === fullMesh) {
    releaseLodConsumer(lodTi);
  }
}
function _detachThinInstanceLodMesh(mesh) {
  const ti = mesh.thinInstances;
  if (!ti) {
    return;
  }
  const source = ti._lodSource;
  const sourceTi = source?.thinInstances;
  if (source && sourceTi?._lodPartner === mesh) {
    sourceTi._lodPartner = null;
    sourceTi._lodDistance = void 0;
    sourceTi._lodBand = void 0;
    sourceTi._lodBuckets = null;
    source._clone = void 0;
  }
  if (ti._lodSource) {
    releaseLodConsumer(ti);
  }
  const partner = ti._lodPartner;
  const partnerTi = partner?.thinInstances;
  ti._lodPartner = null;
  ti._lodDistance = void 0;
  ti._lodBand = void 0;
  ti._lodBuckets = null;
  mesh._clone = void 0;
  if (partner && partnerTi?._lodSource === mesh) {
    partner._clone = void 0;
    releaseLodConsumer(partnerTi);
  }
}
function releaseLodConsumer(lodTi) {
  lodTi._lodSource = null;
  lodTi._lodBuckets = null;
  if (lodTi._lodAutoCull) {
    lodTi._lodAutoCull = false;
    lodTi._gpuCullingEnabled = false;
    lodTi._gpuVersion = -1;
    lodTi._colorGpuVersion = -1;
  }
}

export { _detachThinInstanceLodMesh, addThinInstance, clearThinInstanceLodPartner, enableThinInstanceDynamicDrawCount, enableThinInstanceGpuCulling, flushThinInstances, removeThinInstance, setThinInstanceColor, setThinInstanceColors, setThinInstanceCount, setThinInstanceCullBoundsPad, setThinInstanceDrawCount, setThinInstanceLodPartner, setThinInstanceMatrix, setThinInstances };
//# sourceMappingURL=thin-instance.js.map
